// LB-339 — adults and roles. The client half; worker/lib/adults.js is its twin and the
// SHARED HELPERS block below must stay byte-identical between the two
// (scripts/check-adults.mjs fails if they drift, the same rule coachEnabled.js lives under).
//
// What the client can and cannot know, and why this file is shorter than the worker's:
//
//   - It never sees a PIN. The GET strips every adults[].pin exactly as it strips
//     settings.parentPin, so an adult record here has no `pin` key at all. There is
//     deliberately no pinOf() on this side to be tempted by.
//   - It never migrates. migrateState() must not invent adults for the same reason it has
//     never defaulted parentPin (see src/lib/utils.js): anything this side invents gets
//     sent back on the next save, and a fabricated adults list would overwrite the real
//     one. The worker migrates on read; the client receives the result.
//
// So what lives here is the reading half: who is the Owner, what is this adult allowed to
// do. Nothing outside this module reads `state.adults` directly.

// ---- SHARED HELPERS (keep byte-identical with worker/lib/adults.js) ----
export const MIGRATED_OWNER_ID = "owner";
export const OWNER = "owner";
export const ADULT = "adult";
// The permissions an Owner may grant. Owner-only powers are absent BY DESIGN — see above.
export const PERMISSIONS = ["manageAdults"];
// The PIN a household carries until somebody sets a real one (settings.pinConfigured says
// which). It is a placeholder, never a credential: an INVITED adult must never end up on it,
// because unlike the Owner's row there is no wizard step waiting to replace it — a row left
// here is an account anybody who knows the default can open. See repairPlaceholderAdults.
export const STARTER_PIN = "123456";

export function adultsOf(state) {
  return Array.isArray(state?.adults) ? state.adults : [];
}
export function ownerOf(state) {
  const all = adultsOf(state);
  const byId = state?.settings?.ownerAdultId ? all.find((a) => a && a.id === state.settings.ownerAdultId) : null;
  // Fall back to the role rather than trusting the pointer alone: a household whose
  // ownerAdultId went missing still has exactly one adult carrying role "owner", and
  // answering "nobody is the Owner" would lock every Owner-only route for good.
  return byId || all.find((a) => a && a.role === OWNER) || null;
}
export function adultById(state, adultId) {
  if (!adultId) return null;
  return adultsOf(state).find((a) => a && a.id === adultId) || null;
}
export function isOwner(state, adultId) {
  const owner = ownerOf(state);
  return !!(owner && adultId && owner.id === adultId);
}
export function can(state, adultId, permission) {
  if (!PERMISSIONS.includes(permission)) return false; // not grantable, whatever is stored
  if (isOwner(state, adultId)) return true;            // the Owner has every grantable one
  const adult = adultById(state, adultId);
  return !!(adult && adult.permissions && adult.permissions[permission] === true);
}
// ---- END SHARED HELPERS ----

// The Owner row for a household being CREATED, right now, on this device.
//
// This is the one place the client writes a pin, and it is only safe here: at creation
// there is no stored household to overwrite, which is the same reason defaultState()
// generates the passcode locally. Everywhere else the rule holds — the client never sees a
// pin and never invents one.
//
// It exists because the creating session never re-reads: Root.jsx keeps the state it just
// posted (see handleStart), so the worker's migration — which only runs on a READ — would
// not reach this session, and the wizard's PIN step would have no adult to write to. The id
// matches the migration's so every household's Owner is the same row whichever way it got
// there, and the "123456" placeholder matches what settings.pinConfigured: false means.
export function newOwnerAdult({ name, email } = {}) {
  return {
    id: MIGRATED_OWNER_ID,
    name: typeof name === "string" ? name : "",
    email: typeof email === "string" ? email : null,
    role: OWNER,
    permissions: {},
    pin: "123456",
    addedAt: new Date().toISOString(),
    addedBy: null,
  };
}

// Which adult this device's Parent Mode should ask for (LB-340, screens D1/D2/D3). A device
// records `ownerAdultId` when it was proven by someone's PIN; a device proven by passcode or
// QR has none, because those routes cannot say who was holding it, and a shared family
// device asks for the Owner's PIN. Falls back to the Owner whenever the stored id names
// somebody who is no longer in the household — an adult who has been removed must not leave
// a device with nobody to log in as.
export function adultForDevice(state, deviceOwnerAdultId) {
  return adultById(state, deviceOwnerAdultId) || ownerOf(state);
}

// The others, for "Log in as {name}". Sorted Owner first, then as stored, so the link on a
// two-adult household always names the same person in the same order.
export function otherAdults(state, adultId) {
  const owner = ownerOf(state);
  return adultsOf(state)
    .filter((a) => a && a.id !== adultId)
    .sort((a, b) => (a.id === owner?.id ? -1 : b.id === owner?.id ? 1 : 0));
}

// What to call an adult on screen when their name was never filled in. LB-337 made the
// owner's name optional and every surface that reads it falls back rather than nagging
// (principle 5) — this is that fallback, in one place.
export function adultLabel(adult) {
  if (!adult) return "";
  const name = typeof adult.name === "string" ? adult.name.trim() : "";
  if (name) return name;
  const email = typeof adult.email === "string" ? adult.email.trim() : "";
  return email ? email.split("@")[0] : "";
}

// ---- LB-343: attribution. Who did this, and what the child sees. ----
//
// Transactions and requests have always carried `deviceId`, which answers "which phone" and
// not "which person" — and on a shared family phone those are different questions. From now
// on every write also carries `adultId` AND `adultName`.
//
// Two fields, not one, and the second is the load-bearing half (Alan, 18 Sep):
// **history is history.** The name is copied in at write time, so removing an adult can
// delete their row outright without turning "Grandma said yes" into "A parent said yes" on
// a screen a child already read. The trade is that renaming an adult does not rewrite their
// old entries, which is correct for a record of who did what at the time.

// Who is acting, right now, on this device. The adult who unlocked Parent Mode this session
// wins; failing that, the adult this device belongs to.
//
// Returns null rather than falling back to the Owner. Attributing an action to a named person
// who may not have done it is worse than not naming anyone — the read side below has an
// honest fallback and this does not need to invent one (principle 5).
export function actingAdult(state, activeAdultId, deviceOwnerAdultId) {
  const adult = adultById(state, activeAdultId) || adultById(state, deviceOwnerAdultId);
  if (!adult) return null;
  const name = adultLabel(adult);
  return { id: adult.id, name };
}

// Stamp a row with who did it. Used at every write site so the shape is decided in one place.
// A null actor writes nothing at all, rather than null fields that read as "we asked and the
// answer was nobody".
export function stampActor(row, acting) {
  if (!row || !acting || !acting.id) return row;
  row.adultId = acting.id;
  if (acting.name) row.adultName = acting.name;
  return row;
}

// The name to show for a row, in the order the brief sets out. Returns "" when nothing can be
// said, and the caller renders "A parent" through ct() — so the words stay in the CMS and the
// fallback is never a blank on screen.
//
// `deviceOwners` (deviceId -> adultId) is optional and only a PARENT surface supplies it: it
// comes from the devices list, which is behind requireDevice, and a kid-mode screen has no
// business calling that route. Without it an older row falls straight through to "A parent",
// which is the honest answer on a surface that cannot look it up.
export function attributionName(state, row, deviceOwners = null) {
  if (!row) return "";
  // 1. The name written at the time. Authoritative, and survives the adult being removed.
  if (typeof row.adultName === "string" && row.adultName.trim()) return row.adultName.trim();
  // 2. The adult the row names, if they are still in the household.
  const named = row.adultId ? adultById(state, row.adultId) : null;
  if (named) { const n = adultLabel(named); if (n) return n; }
  // 3. An older row, from before this ticket: whoever owns the device it was written on.
  if (row.deviceId && deviceOwners) {
    const ownerId = deviceOwners[row.deviceId];
    const owner = ownerId ? adultById(state, ownerId) : null;
    if (owner) { const n = adultLabel(owner); if (n) return n; }
  }
  // 4. Older still, or a shared device nobody had unlocked. The caller says "A parent".
  return "";
}

// The acting adult without a household code to hand. Every parent surface that writes an
// attributed row needs the same two ids, and most of them are sub-components several props
// deep from the one place that knows the code — so it is read from where the app already
// keeps it rather than threaded through. Callers that DO have the code use actingAdult.
export function actingAdultHere(state, getActive, getOwner, code) {
  if (!code) return null;
  return actingAdult(state, getActive(code), getOwner(code));
}

// deviceId -> adultId, from the devices list a parent surface already holds. Only a parent
// surface has one: the route is behind requireDevice, and a kid screen must not call it.
export function deviceOwnerMap(devices) {
  const out = {};
  for (const [id, d] of Object.entries(devices || {})) if (d && d.ownerAdultId) out[id] = d.ownerAdultId;
  return out;
}
